'use client'

import { track } from '@amplitude/analytics-browser'
import { Slot } from '@radix-ui/react-slot'

export type InteractedEventName = 'Homepage Interacted' | 'Landing Page Interacted'

/**
 * Cited mirror of the ratified page-interaction contract in
 * `strategy/.docs/analytics/amplitude-events-content-pages.md` — types only
 * the interaction types nexus fires, keyed on `interaction_type` so each
 * payload's required props are checked at compile time. The dictionary is the
 * source of truth; sync this union when its changelog touches these events.
 */
export type InteractionProperties =
    | ({ interaction_type: 'route_clicked'; route_url: string; position: number } & (
          | { ui_source: 'featured_route_card' | 'plan_route_list'; mode: string }
          // C2C interlink items carry no mode; the route path's first segment names it.
          | {
                ui_source: 'depart_route_list' | 'arrive_route_list' | 'popular_route_list'
                mode?: never
            }
      ))
    | { interaction_type: 'carrier_announcement_clicked'; carrier_slug: string; link_url: string }
    | {
          interaction_type: 'carrier_clicked'
          carrier_slug: string
          position: number
          link_url: string
      }
    | { interaction_type: 'promotion_clicked'; promotion_name: string; link_url: string }
    | {
          interaction_type: 'content_link_clicked'
          content_name: string
          link_url: string
          position?: number
          is_outbound?: boolean
      }
    | { interaction_type: 'destination_toggled'; destination: string; expanded: boolean }
    | {
          interaction_type: 'app_download_clicked'
          store: 'app_store' | 'play_store'
          is_outbound: true
      }
    | {
          interaction_type: 'outbound_link_clicked'
          link_url: string
          link_domain: string
          is_outbound: true
      }

/**
 * Fires a page-interaction Amplitude event against the typed contract.
 * Amplitude-only by design — these interaction events have no Snowplow
 * counterpart. `page_type` is added by the page-type enrichment plugin.
 * `TrackInteraction` calls this from the click it attaches; a caller with its
 * own handler calls it directly.
 */
export function trackInteraction(
    eventName: InteractedEventName,
    properties: InteractionProperties,
): void {
    track(eventName, properties)
}

/**
 * The one payload field no caller can supply: a disclosure's state is decided by
 * the click that changes it, so it is unknowable at render — on the server or
 * the client. `TrackInteraction` fills it, and the prop names the *field* rather
 * than carrying a value, which keeps it a string across the server boundary
 * (React cannot serialize a function: "Functions cannot be passed directly to
 * Client Components").
 */
type DisclosureStateField = 'expanded'

type ToggleProperties = Extract<InteractionProperties, { interaction_type: 'destination_toggled' }>

/**
 * Props, split so the disclosure capture is **required exactly where it applies
 * and forbidden everywhere else** — `destination_toggled` cannot be fired
 * without it, and no other interaction type can ask for it. `expanded` is absent
 * from the payload the caller passes, so it cannot be hand-supplied with a value
 * that the click would contradict.
 */
type TrackInteractionProps = {
    /**
     * Amplitude event to fire. Shared components rendered on landing pages pass
     * `'Landing Page Interacted'`; the two events share one property contract,
     * so only the name differs.
     */
    eventName?: InteractedEventName
    /** Exactly one element — the one this attaches tracking to. */
    children: React.ReactElement
} & (
    | {
          /** The interaction payload, sent verbatim. */
          properties: Exclude<InteractionProperties, ToggleProperties>
          captureDisclosureState?: never
      }
    | {
          properties: Omit<ToggleProperties, DisclosureStateField>
          /** Names the field this fills from the clicked disclosure's state. */
          captureDisclosureState: DisclosureStateField
      }
)

/**
 * The state a disclosure is moving *to* on this click — read generically, so this
 * serves a real ARIA disclosure and a native `<details>` alike.
 *
 * Both are read *before* the state flips, which is what makes the click (not
 * `toggle`) the usable signal: `toggle` does not bubble, and it fires after the
 * change. A native `<summary>` exposes no `aria-expanded` attribute at all: in
 * Chrome, `getAttribute('aria-expanded')` and `.ariaExpanded` are both `null`
 * before and after toggling, because the browser computes the
 * state into the accessibility tree from `details.open` and never reflects it to
 * the DOM. So ARIA first (an authored disclosure button), `details.open` second.
 */
function nextDisclosureState(element: HTMLElement): boolean {
    const aria = element.getAttribute('aria-expanded')
    if (aria !== null) {
        return aria !== 'true'
    }
    // Attach to the `<summary>`, never the `<details>`: a click anywhere in an
    // open disclosure's body bubbles to the `<details>`, so wrapping that would
    // fire the event on every link inside it. The `<summary>` is the toggle
    // affordance, and `Enter`/`Space` on it dispatch a click too.
    //
    // Read from the element's *own* `<details>`, not `closest('details')`, for
    // the mirror reason: a descendant of an open disclosure would otherwise
    // report that ancestor's state as its own — silently, and inverted.
    const details = element.tagName === 'SUMMARY' ? element.parentElement : null
    return details instanceof HTMLDetailsElement ? !details.open : false
}

/**
 * Tracking-only client island: it attaches to an element the caller renders
 * rather than rendering one itself.
 *
 * That inversion is the point. A component that *builds* the link forces its
 * caller to hand the destination across the server→client boundary, where it is
 * serialized into the flight payload before any locale prefix is applied.
 * Attaching instead, a server caller's `Link` renders on the server (next-intl
 * resolves the prefix through its react-server entry) and the payload carries
 * the finished `<a href="/en-us/…">`.
 *
 * It renders **no element of its own** — `Slot` merges the handler onto the
 * child, which is what lets it wrap a `<summary>`: a `<details>` is a disclosure
 * only while `<summary>` is its first DOM child, and a wrapper would break the
 * control. The child keeps its own `className`, `aria-label` and `href`, stays
 * focusable, and `Enter` still fires the click this listens for.
 */
export const TrackInteraction = ({
    properties,
    eventName = 'Homepage Interacted',
    captureDisclosureState,
    children,
}: TrackInteractionProps) => (
    <Slot
        onClick={(event: React.MouseEvent<HTMLElement>) =>
            trackInteraction(
                eventName,
                // The cast is the seam between a props union TS narrows by
                // `interaction_type` and a runtime merge keyed by a field name:
                // `captureDisclosureState` is only ever present on the branch
                // whose payload is missing exactly that field.
                captureDisclosureState
                    ? ({
                          ...properties,
                          [captureDisclosureState]: nextDisclosureState(event.currentTarget),
                      } as InteractionProperties)
                    : (properties as InteractionProperties),
            )
        }
    >
        {children}
    </Slot>
)
